<template>
  <v-container fluid class="admin-funnel">
    <NcrmHomePageHeader
      icon="mdi-filter-variant"
      :title="$t('admin.analytics.funnel.title')"
      :subtitle="$t('admin.analytics.funnel.subtitle')"
    />

    <v-alert v-if="errorMessage" type="error" class="mt-4">{{ errorMessage }}</v-alert>

    <div class="admin-funnel__period mt-4">
      <NcrmSegmentedControl
        :model-value="days"
        :items="periodItems"
        :aria-label="$t('admin.analytics.funnel.period')"
        @update:model-value="days = $event"
      />
    </div>

    <NcrmHomeKpiCards class="mt-4" :items="kpiItems" />

    <v-card variant="flat" class="admin-funnel__panel mt-4">
      <div class="admin-funnel__panel-head">
        <h3>{{ $t("admin.analytics.funnel.stepsTitle") }}</h3>
        <p>{{ $t("admin.analytics.funnel.stepsHint") }}</p>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <div v-else-if="!report || !report.totals.started" class="admin-funnel__empty">
        {{ $t("admin.analytics.funnel.empty") }}
      </div>
      <!-- Un funnel per metodă de autentificare. Google sare peste pașii de
           email, deci un tabel comun ar arăta o prăbușire care nu există. -->
      <div v-else class="admin-funnel__segments">
        <section
          v-for="segment in visibleSegments"
          :key="segment.key"
          class="admin-funnel__segment"
        >
          <div class="admin-funnel__segment-head">
            <h4>{{ $t(`admin.analytics.funnel.segments.${segment.key}`) }}</h4>
            <span class="admin-funnel__segment-total">
              {{ $t("admin.analytics.funnel.segmentTotal", {
                started: segment.totals.started,
                conversion: conversion(segment.totals.conversion),
              }) }}
            </span>
          </div>
          <ul class="admin-funnel__steps">
            <li v-for="step in segment.steps" :key="step.key">
              <div class="admin-funnel__step-head">
                <span>{{ $t(`admin.analytics.funnel.steps.${step.key}`) }}</span>
                <strong v-if="step.applicable">{{ step.count }}</strong>
                <span v-else class="admin-funnel__not-applicable">
                  {{ $t("admin.analytics.funnel.notApplicable") }}
                </span>
              </div>
              <template v-if="step.applicable">
                <div class="admin-funnel__bar">
                  <div
                    class="admin-funnel__bar-fill"
                    :style="{ width: `${barWidth(step, segment)}%` }"
                  ></div>
                </div>
                <div class="admin-funnel__step-foot">
                  <span>{{ $t("admin.analytics.funnel.ofStart", { value: conversion(step.conversion_from_start) }) }}</span>
                  <!-- The drop is the number that matters: it names the step where
                       people actually stop. -->
                  <span v-if="step.dropped_from_previous" class="admin-funnel__drop">
                    {{ $t("admin.analytics.funnel.dropped", { count: step.dropped_from_previous }) }}
                  </span>
                </div>
              </template>
            </li>
          </ul>
        </section>
      </div>
    </v-card>

    <v-row dense class="mt-4">
      <v-col cols="12" md="6">
        <v-card variant="flat" class="admin-funnel__panel h-100">
          <div class="admin-funnel__panel-head">
            <h3>{{ $t("admin.analytics.funnel.failuresTitle") }}</h3>
            <p>{{ $t("admin.analytics.funnel.failuresHint") }}</p>
          </div>
          <ul class="admin-funnel__reasons">
            <li v-for="row in failures" :key="`${row.stage}:${row.error_code}`">
              <span>{{ row.stage }} · {{ row.error_code }}</span><strong>{{ row.total }}</strong>
            </li>
            <li v-if="!failures.length" class="admin-funnel__none">
              {{ $t("admin.analytics.funnel.noFailures") }}
            </li>
          </ul>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card variant="flat" class="admin-funnel__panel h-100">
          <div class="admin-funnel__panel-head">
            <h3>{{ $t("admin.analytics.funnel.blockedTitle") }}</h3>
            <p>{{ $t("admin.analytics.funnel.blockedHint") }}</p>
          </div>
          <ul class="admin-funnel__reasons">
            <li v-for="row in blocked" :key="row.reason">
              <span>{{ $t(`admin.analytics.funnel.reasons.${row.reason}`) }}</span><strong>{{ row.total }}</strong>
            </li>
            <li v-if="!blocked.length" class="admin-funnel__none">
              {{ $t("admin.analytics.funnel.noBlocked") }}
            </li>
          </ul>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import NcrmHomeKpiCards from "@/components/ncrm/shared/NcrmHomeKpiCards.vue";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import NcrmSegmentedControl from "@/components/ncrm/shared/NcrmSegmentedControl.vue";
import { fetchRegistrationFunnel, formatConversion, stepWidth } from "./analytics/adminAnalyticsApi.mjs";

export default {
  name: "AdminRegistrationFunnelHome",
  components: { NcrmHomeKpiCards, NcrmHomePageHeader, NcrmSegmentedControl },
  data() {
    return {
      loading: false,
      errorMessage: "",
      report: null,
      days: 30,
      periodOptions: [7, 30, 90],
    };
  },
  computed: {
    periodItems() {
      return this.periodOptions.map((option) => ({ value: option, label: this.$t("admin.analytics.funnel.days", { days: option }) }));
    },
    failures() {
      return this.report?.failures || [];
    },
    blocked() {
      return this.report?.blocked || [];
    },
    // Un segment fără nicio pornire nu spune nimic; cât timp Google este
    // dezactivat, raportul nu trebuie să afișeze un funnel gol lângă cel real.
    visibleSegments() {
      const segments = this.report?.segments || [];
      const withStarts = segments.filter((segment) => segment.totals.started > 0);
      return withStarts.length ? withStarts : segments.slice(0, 1);
    },
    kpiItems() {
      const totals = this.report?.totals || {};
      return [
        { key: "started", label: this.$t("admin.analytics.funnel.metrics.started"), value: totals.started || 0, icon: "mdi-account-plus-outline", color: "primary" },
        { key: "completed", label: this.$t("admin.analytics.funnel.metrics.completed"), value: totals.completed || 0, icon: "mdi-check-circle-outline", color: "success" },
        { key: "conversion", label: this.$t("admin.analytics.funnel.metrics.conversion"), value: totals.conversion || 0, displayValue: formatConversion(totals.conversion), icon: "mdi-percent-outline", color: "info" },
        { key: "blocked", label: this.$t("admin.analytics.funnel.metrics.blocked"), value: totals.blocked || 0, icon: "mdi-shield-alert-outline", color: "warning" },
      ];
    },
  },
  watch: {
    days: "loadReport",
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    conversion: formatConversion,
    // Bara se raportează la pornirile PROPRIULUI segment, nu la totalul general:
    // altfel calea cu mai puține porniri ar arăta artificial de scurtă.
    barWidth(step, segment) {
      return stepWidth(step, segment?.totals?.started || 0);
    },
    async loadReport() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const { data } = await fetchRegistrationFunnel(this.days);
        this.report = data;
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || this.$t("admin.analytics.funnel.errors.load");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-funnel { min-height: 100%; background: var(--app-page-background); }
.admin-funnel__panel { overflow: hidden; border: 1px solid rgba(var(--v-theme-on-surface), 0.08); border-radius: var(--app-radius-base); padding: 20px; }
.admin-funnel__panel-head h3 { margin: 0; color: var(--app-text-strong); font-size: 1rem; }
.admin-funnel__panel-head p { margin: 4px 0 14px; color: var(--app-text-default); font-size: 0.78rem; opacity: 0.7; line-height: 1.45; }
.admin-funnel__steps { margin: 0; padding: 0; list-style: none; display: grid; gap: 14px; }
.admin-funnel__step-head, .admin-funnel__step-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.admin-funnel__step-head span { color: var(--app-text-strong); font-size: 0.86rem; }
.admin-funnel__step-head strong { color: var(--app-text-strong); font-size: 0.95rem; }
.admin-funnel__step-foot { margin-top: 4px; color: var(--app-text-default); font-size: 0.74rem; opacity: 0.72; }
.admin-funnel__drop { color: rgb(var(--v-theme-error)); opacity: 0.9; }
.admin-funnel__bar { height: 10px; border-radius: 999px; background: rgba(var(--v-theme-on-surface), 0.07); overflow: hidden; }
.admin-funnel__bar-fill { height: 100%; border-radius: 999px; background: rgb(var(--v-theme-primary)); transition: width 0.25s ease; }
.admin-funnel__reasons { margin: 0; padding: 0; list-style: none; }
.admin-funnel__reasons li { display: flex; justify-content: space-between; gap: 12px; padding: 6px 0; border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06); font-size: 0.8rem; }
.admin-funnel__reasons span { color: var(--app-text-default); opacity: 0.8; }
.admin-funnel__reasons strong { color: var(--app-text-strong); }
.admin-funnel__none, .admin-funnel__empty { color: var(--app-text-default); font-size: 0.8rem; opacity: 0.7; border-bottom: 0; }
.admin-funnel__empty { padding: 8px 0; }
.admin-funnel__period { display: flex; justify-content: flex-end; }
</style>
